import { Box } from "@mui/material";
import React, { Component, ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { useSearchParams } from "react-router-dom";
import axiosInstant from "../../../../api";
import { getProductForBrand } from "../../../../api/endpoints";
import { MainContainer } from "../../../../components/ContentWrapper";
import { setPageTitle } from "../../../../components/PageTitle";
import { useNetworkScope } from "../../../../hooks/useNetworkScope";
import { ISingleProduct } from "../../productsV2/productListView/constants";
import { ProductV2Modal } from "../../productsV2/productModal";
import {
  classifyError,
  keepStableBrands,
  listParamsOnLeave,
  openVideoId,
  pagesChipCount,
  videoViewChange,
} from "./pageModel";
import type { PageMessageKind, VideoView } from "./pageModel";
import { ProductHeader, PRODUCT_TITLE_ID } from "./components/ProductHeader";
import { ProductHeaderMenu } from "./components/ProductHeaderMenu";
import { HAND_SCROLL_EVENTS, SectionRail, SECTION_IDS, SECTION_ORDER } from "./components/SectionRail";
import type { RailChip } from "./components/SectionRail";
import { ProductDetailSkeleton, ProductPageMessage } from "./components/PageStates";
import { ConsumerVoiceSection } from "./consumerVoice/ConsumerVoiceSection";
import { AiPresenceSection } from "./aiPresence/AiPresenceSection";
import { ProductPagesSection } from "./productPages/ProductPagesSection";
import { VideosSection } from "./videos/VideosSection";

/**
 * The product details page. Mounted by pages/admin/productsV2/Products.tsx
 * (brand and network, `?productId=`) and pages/products/SingleProduct.tsx
 * (internal, `/products/:brandId?productId=`) with the props both pass to
 * productDetail/index.tsx, which stays in the tree for rollback (an import
 * swap; its extra `onUpdate` is passed by neither).
 *
 * One page, sections in D1 order: header, rail, Consumer Voice, Product
 * pages & audits, Videos.
 */

interface ISelectedProduct {
  brandId: number;
  productId: number;
  onClose?: () => void;
}

// One rail chip per section, in DOM order (SECTION_ORDER, D1). The skeleton
// draws the same list, so nothing jumps when the product lands.
const RAIL_LABELS: Record<(typeof SECTION_ORDER)[number], string> = {
  [SECTION_IDS.consumerVoice]: "Consumer Voice",
  [SECTION_IDS.aiPresence]: "AI Presence",
  [SECTION_IDS.pages]: "Product pages",
  [SECTION_IDS.videos]: "Videos",
};

/** Which product a loaded header or a failed load belongs to. */
const keyOf = (brandId: number, productId: number) => `${brandId}:${productId}`;

/** How long a restored position is held while the page below or above it is still loading. */
const HOLD_MS = 5000;

/**
 * Puts `el`'s top at `top` px from the top of the screen, and keeps it there
 * as the page column around it changes size (a ResizeObserver) for HOLD_MS,
 * or until the reader scrolls by hand (HAND_SCROLL_EVENTS, the rail's). The
 * page is often still growing when the scroll is restored: the Videos list
 * remounts while Stories is picked and loads again when the video closes, and
 * a hidden section may still be re-reading. Returns the stop.
 */
const holdTop = (el: HTMLElement, top: number): (() => void) => {
  const put = () => {
    const off = el.getBoundingClientRect().top - top;
    if (Math.abs(off) >= 1) window.scrollBy(0, off);
  };
  put();
  const column = el.parentElement;
  if (!column || typeof ResizeObserver === "undefined") return () => {};
  const observer = new ResizeObserver(put);
  let timer = 0;
  const stop = () => {
    observer.disconnect();
    window.clearTimeout(timer);
    HAND_SCROLL_EVENTS.forEach((type) => window.removeEventListener(type, stop, { capture: true }));
  };
  observer.observe(column);
  HAND_SCROLL_EVENTS.forEach((type) => window.addEventListener(type, stop, { capture: true, passive: true }));
  timer = window.setTimeout(stop, HOLD_MS);
  return stop;
};

/**
 * Calls `read` inside React's commit, before the DOM changes, whenever
 * `watch` changed: getSnapshotBeforeUpdate, which hooks don't have. A layout
 * effect runs after the DOM changed, too late to measure what the reader saw
 * (the scroll rule below).
 */
class BeforeCommit extends Component<{ watch: string; read: () => void }> {
  getSnapshotBeforeUpdate(prev: Readonly<{ watch: string }>) {
    const { watch, read } = this.props;
    if (prev.watch !== watch) read();
    return null;
  }

  // React only calls getSnapshotBeforeUpdate on a class that has this too.
  componentDidUpdate() {}

  render() {
    return null;
  }
}

export function SelectedProduct(props: ISelectedProduct) {
  const { brandId, productId, onClose } = props;
  const pageKey = keyOf(brandId, productId);
  // Each result carries the product it was loaded for. Both mounts re-render
  // this page in place when productId or brandId changes, so the first render
  // under the new URL finds a mismatch and draws the skeleton, never the
  // previous product's header, menu or message.
  const [loaded, setLoaded] = useState<{ key: string; product: ISingleProduct } | null>(null);
  // The header call's failure, shown only while there is no product. A failed
  // SILENT refetch keeps the last good product instead.
  const [failed, setFailed] = useState<{ key: string; kind: PageMessageKind } | null>(null);
  const product = loaded?.key === pageKey ? loaded.product : null;
  const pageError = failed?.key === pageKey ? failed.kind : null;
  const [pageAttempt, setPageAttempt] = useState(0);
  const retryPage = useCallback(() => setPageAttempt((n) => n + 1), []);
  // Bumped when the settings modal closes or completes: the modal edits
  // product pages and starts Consumer Voice runs, so both sections re-read in
  // place (their `reloadKey`). The rail re-picks its chip on it too (refreshKey).
  const [sectionsNonce, setSectionsNonce] = useState(0);
  // Product pages' count for its rail chip (`onCount`), null while unknown.
  const [pagesCount, setPagesCount] = useState<number | null>(null);
  // Consumer Voice's fallback brand, as the settings modal takes it.
  const { brandId: scopeBrandId } = useNetworkScope();
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Videos | Stories, held here as today's page held it (productDetail/index.tsx:23).
  const [showVideosPanel, setShowVideosPanel] = useState(true);
  // Drops a response that an unmount, a newer fetch or a product switch overtook.
  const seq = useRef(0);

  const [searchParams, setSearchParams] = useSearchParams();
  // Q1 (b): while the Videos block shows a video, the page is the header and
  // that section only. The rail and the other sections are hidden, not
  // unmounted, so closing the video doesn't refetch them.
  const videoOpen = showVideosPanel && openVideoId(searchParams.get("video")) != null;
  const offWhileVideo = videoOpen ? "hidden" : undefined;

  // The same params the nested view's own Back drops (VideoSection.tsx:265-270).
  const closeVideo = useCallback(
    () =>
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("video");
        next.delete("selectedTab");
        return next;
      }),
    [setSearchParams],
  );

  // The product list's search as it was when this product opened (the list
  // keeps every param when it opens one, productListView/index.tsx:147-149).
  // Leaving puts it back, whatever the Videos list's "Search" chip did to it.
  const [listSearch] = useState(() => searchParams.get("search"));
  // Read by the scroll rule below: leaving restores nothing on the way out.
  const leavingRef = useRef(false);

  // Leaving the product (Back, the skeleton's Back, the message's Go back,
  // after Delete product) is one navigation and one render, straight to the
  // list:
  //   - the internal mount's onClose goes to /products (SingleProduct.tsx), a
  //     page with a URL of its own: nothing to add;
  //   - the brand / network mount's drops productId only, on this same path
  //     (Products.tsx:103-111), and the list keeps every param when it opens
  //     a product (productListView/index.tsx:147-149). So the Videos list's
  //     state (filters, sort, Load more, `?video=`) would open the next
  //     product with it. It goes here, as a replace of the entry onClose just
  //     pushed (listParamsOnLeave). The product-page tab is in the hash,
  //     which any router navigation drops.
  // onClose goes FIRST: its setSearchParams builds on its own last render's
  // params (react-router-dom 6.30), so called after this one it would put
  // every param back. Its navigation has landed by the time it returns (the
  // data router has no loaders to wait for), so the path tells the mounts
  // apart. Batched because a delete calls this from a `.then`, which React 17
  // doesn't batch: each navigation would render on its own, the page once
  // more at the list URL, firing a Videos list request, before it unmounts.
  const leave = useCallback(() => {
    if (!onClose) return;
    leavingRef.current = true;
    const from = window.location.pathname;
    unstable_batchedUpdates(() => {
      onClose();
      if (window.location.pathname !== from) return;
      setSearchParams((prev) => listParamsOnLeave(prev, listSearch), { replace: true });
    });
  }, [onClose, setSearchParams, listSearch]);
  const back = onClose ? leave : undefined;
  // The header's Back, and the skeleton's standing in for it: with a video
  // showing, it closes the video — a level up, not the product.
  const headerBack = videoOpen ? closeVideo : back;
  const headerBackLabel = videoOpen ? "Back to product" : undefined;

  // Opening a video hides the rail, Consumer Voice and Product pages (Q1 b)
  // but the window keeps its scroll, so the video view would open far above
  // the screen (the reader's bottom showing), and closing it would bring those
  // sections back above the grid and leave the grid far below the fold.
  // Today's page opens the video in place under its header and returns to
  // the grid. The Videos section is the anchor: what is kept is where its top
  // sat on screen, read just before the commit (BeforeCommit), so a hidden
  // section whose height changed meanwhile (a page added in Settings, a
  // Product pages tab, a reload) doesn't move the rows the reader returns to.
  // Before paint (videoViewChange):
  //   - open (a click, the browser's Forward): remember the section's top,
  //     then bring it into view if it now sits above the screen (its
  //     scroll-mt-[76px] clears the sticky TopBar);
  //   - close (the nested Back, the header's "Back to product", the
  //     browser's Back): put the section's top back where it was at the open,
  //     so the grid rows are where they were left;
  //   - Videos | Stories with `?video=` still set (brand users; the tabs sit
  //     above the video): neither. The other sections come or go above the
  //     tabs just clicked, so the section's top stays where it was, and the
  //     open's position is kept for the real close. Until the next change the
  //     section is at least as tall as the screen below that top
  //     (`videosFloor`): a short Stories list would otherwise leave the page
  //     too short to scroll there, and the tabs would drop down the screen.
  // A link that mounts with `?video=` set has no grid position to go back to:
  // on close, a reader who had scrolled into the video gets the Videos section
  // (the grid) at the top of the screen, and one still at the top of the page
  // stays there. Leaving the product restores nothing. A kept top is held
  // while the page settles (holdTop): the grid may still be loading, a hidden
  // section still re-reading.
  // Of this rule, VideosSection takes only the floor: VideoSection swaps grid
  // and video in the same commit as this page, so the grid is back in the DOM
  // when this runs.
  const beforeTop = useRef<number | null>(null);
  const readBeforeTop = useCallback(() => {
    beforeTop.current = document.getElementById(SECTION_IDS.videos)?.getBoundingClientRect().top ?? null;
  }, []);
  const prevView = useRef<VideoView | null>(null);
  // The section's top when the video opened; null for a link that mounted with it.
  const openTop = useRef<number | null>(null);
  // The running hold's stop: the next change, or leaving, ends it.
  const stopHold = useRef<(() => void) | null>(null);
  useEffect(() => () => stopHold.current?.(), []);
  const [videosFloor, setVideosFloor] = useState<number | null>(null);
  useLayoutEffect(() => {
    const view = { videoOpen, stories: !showVideosPanel };
    const change = videoViewChange(prevView.current, view);
    prevView.current = view;
    const before = beforeTop.current;
    beforeTop.current = null;
    const videos = document.getElementById(SECTION_IDS.videos);
    // Any Videos | Stories or `?video=` change ends the last hold and floor
    // (the browser's Back can close the video while Stories shows).
    stopHold.current?.();
    stopHold.current = null;
    const tabTop = change === "tab" && videos && !leavingRef.current ? before : null;
    // Set in a layout effect, so it renders before paint; the hold's
    // ResizeObserver puts the top back once the floor has made room.
    setVideosFloor(tabTop != null ? Math.ceil(window.innerHeight - tabTop) : null);
    if (!change || leavingRef.current || !videos) return;
    if (change === "tab") {
      if (tabTop != null) stopHold.current = holdTop(videos, tabTop);
      return;
    }
    // Its scroll-mt-[76px]: the top of the screen, clear of the sticky TopBar.
    const clearance = parseFloat(getComputedStyle(videos).scrollMarginTop) || 0;
    if (change === "open") {
      openTop.current = before;
      if (videos.getBoundingClientRect().top < clearance) videos.scrollIntoView({ block: "start" });
      return;
    }
    const saved = openTop.current;
    openTop.current = null;
    // The grid of a link that mounted with `?video=` only starts loading now.
    if (saved != null) stopHold.current = holdTop(videos, saved);
    else if (window.scrollY > 0) stopHold.current = holdTop(videos, clearance);
  }, [videoOpen, showVideosPanel]);

  const fetchProduct = useCallback(
    async (silent: boolean) => {
      seq.current += 1;
      const mine = seq.current;
      const key = keyOf(brandId, productId);
      try {
        const { data } = await axiosInstant.get(getProductForBrand(brandId.toString(), productId.toString()));
        if (mine !== seq.current) return;
        if (data) {
          setLoaded((prev) => ({
            key,
            product: keepStableBrands(prev?.key === key ? prev.product : null, data as ISingleProduct),
          }));
          setFailed(null);
        } else if (!silent) {
          // Today an empty body left the loader up forever.
          setFailed({ key, kind: "error" });
        }
      } catch (err) {
        if (mine !== seq.current) return;
        const kind = classifyError(err);
        // A 401 or the expired-token cancel: the app is already redirecting to
        // login — change nothing. Silent: keep showing the last good product.
        if (kind === "none" || silent) return;
        setFailed({ key, kind });
      }
    },
    [brandId, productId],
  );

  useEffect(() => {
    // Try again starts from the skeleton. (A product switch already does:
    // the old results no longer match pageKey.) The sections remount under
    // the new product, so the old page count goes with them, and so does an
    // open settings modal: it was opened on the old product, and would open
    // again over the next one once it lands.
    setLoaded(null);
    setFailed(null);
    setPagesCount(null);
    setSettingsOpen(false);
    fetchProduct(false);
    return () => {
      seq.current += 1;
    };
  }, [fetchProduct, pageAttempt]);

  // The page owns the tab title: the nested video view never writes it, so
  // closing a video leaves the product's name in place.
  const title = product?.title;
  useEffect(() => {
    setPageTitle(title);
  }, [title]);

  // The modal's X, backdrop and Esc (MODAL/index.tsx:104,133) and a General
  // save (:160) all re-read the header silently and reload the sections.
  // productDetail/Header.tsx never passed onUpdate to its menu, so a save
  // doesn't call props.onUpdate here either.
  const closeSettings = useCallback(() => {
    setSettingsOpen(false);
    fetchProduct(true);
    setSectionsNonce((n) => n + 1);
  }, [fetchProduct]);
  const openSettings = useCallback(() => setSettingsOpen(true), []);

  // Consumer Voice never carries a number; Product pages shows its count at
  // 2 or more; Videos has none (D4).
  const railChips = useMemo<RailChip[]>(
    () =>
      SECTION_ORDER.map((id) => ({
        id,
        label: RAIL_LABELS[id],
        count: id === SECTION_IDS.pages ? pagesChipCount(pagesCount) : undefined,
      })),
    [pagesCount],
  );

  // What the page's <main> shows, in this order: the header call's failure,
  // the skeleton until the product lands, the product.
  let body: ReactNode;
  if (pageError) {
    body = <ProductPageMessage kind={pageError} onBack={back} onRetry={retryPage} productId={productId} />;
  } else if (!product) {
    // With a video open, the skeleton is what the loaded page will be: the
    // header (Back closes the video) and the Videos heading row.
    body = <ProductDetailSkeleton onBack={headerBack} backLabel={headerBackLabel} videoOpen={videoOpen} />;
  } else {
    // An article inside the page's <main>, so the product <header> is its
    // header rather than a second page banner beside the TopBar's AppBar.
    body = (
      <Box component="article" aria-labelledby={PRODUCT_TITLE_ID} className="flex flex-col gap-5">
        <ProductHeader
          product={product}
          onBack={headerBack}
          backLabel={headerBackLabel}
          menu={<ProductHeaderMenu product={product} onSettings={openSettings} onDelete={back} />}
        />

        <SectionRail chips={railChips} refreshKey={sectionsNonce} className={offWhileVideo} />

        {/* Hidden, not unmounted, while a video is open (Q1 b): closing it
            doesn't refetch them. Both re-read in place on sectionsNonce, and
            hold their background polls while the settings modal is open. */}
        <ProductPagesSection
          product={product}
          reloadKey={sectionsNonce}
          pollPaused={settingsOpen}
          onCount={setPagesCount}
          className={offWhileVideo}
        />
        <ConsumerVoiceSection
          product={product}
          scopeBrandId={scopeBrandId}
          reloadKey={sectionsNonce}
          pollPaused={settingsOpen}
          className={offWhileVideo}
        />
        <AiPresenceSection
          product={product}
          scopeBrandId={scopeBrandId}
          reloadKey={sectionsNonce}
          pollPaused={settingsOpen}
          className={offWhileVideo}
        />

        <VideosSection
          brandId={brandId}
          showVideosPanel={showVideosPanel}
          onShowVideosPanel={setShowVideosPanel}
          videoOpen={videoOpen}
          minHeight={videosFloor}
        />
      </Box>
    );
  }

  // One tree for every state: BeforeCommit (first in MainContainer) and the
  // <main> keep their places, so they stay mounted when the skeleton or a
  // message gives way to the page. The <main> is the page's: neither the app
  // shell nor MainContainer renders one, and the product is its main content.
  return (
    <MainContainer>
      <BeforeCommit watch={`${videoOpen}|${showVideosPanel}`} read={readBeforeTop} />
      <Box component="main">{body}</Box>

      {/* Opened as the list's menu opens it (productListView/ProductMenu.tsx:80-90):
          the product only, so it lands on General and falls back to the scope
          brand. Over the loaded page only. */}
      {settingsOpen && !pageError && product && (
        <ProductV2Modal product={product} onClose={closeSettings} onComplete={closeSettings} />
      )}
    </MainContainer>
  );
}
